import React, { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { Badge, Carte, Cellule, Modale, Puces, Recherche, Section, Tableau } from "./ui";

/* Le suivi de l'Aide de la console (19.10.2026) : ce que l'équipe demande à l'Aide (le bouton
   en bas à droite), pour voir ce qui bloque et compléter le guide. Réglages → « Suivi de
   l'Aide », à côté d'Outils ; le serveur ne répond qu'à un compte administrateur. */
const ZURICH = "Europe/Zurich";
const quand = (iso) => {
  const d = new Date(iso || "");
  if (!iso || Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: ZURICH })} à ${d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", timeZone: ZURICH })}`;
};
const dollars = (n) => `${(n || 0).toFixed(2)} $`;

export default function SuiviAide() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [vue, setVue] = useState("conversations");
  const [cherche, setCherche] = useState("");
  const [sansReponse, setSansReponse] = useState(null);
  const [ouverte, setOuverte] = useState(null);

  useEffect(() => {
    api("/assistant-interne/conversations").then(setD).catch((e) => setErreur(e.message));
    api("/assistant-interne/questions-sans-reponse").then((r) => setSansReponse(r.questions || [])).catch(() => setSansReponse([]));
  }, []);

  const mot = cherche.trim().toLowerCase();
  const lignes = useMemo(() => (d?.items || []).filter((c) => !mot
    || [c.par, c.premiere, ...(c.ecrans || [])].join(" ").toLowerCase().includes(mot)), [d, mot]);
  const questions = (sansReponse || []).filter((q) => !mot || `${q.question} ${q.par}`.toLowerCase().includes(mot));
  const ouvrir = (c) => api(`/assistant-interne/conversations/${encodeURIComponent(c.id)}`)
    .then(setOuverte).catch((e) => setErreur(e.message));
  const r = d?.resume || {};

  return (
    <div className="space-y-8" data-testid="suivi-aide">

      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
      {!d ? (!erreur && <p className="text-[var(--muted)]">Chargement…</p>) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="suivi-aide-cartes">
            <Carte titre="Conversations (30 jours)" valeur={r.conversations_30j || 0}
              detail={(r.personnes_30j || []).join(", ") || "personne"} testid="suivi-aide-conversations" />
            <Carte titre="Questions (30 jours)" valeur={r.questions_30j || 0} ton="primaire" testid="suivi-aide-questions" />
            <Carte titre="Pas dans le guide (30 jours)" valeur={r.sans_reponse_30j || 0}
              detail="à ajouter au guide" ton={r.sans_reponse_30j ? "ambre" : "neutre"} testid="suivi-aide-sans-reponse" />
            <Carte titre="Coût (30 jours)" valeur={dollars(r.cout_30j)} detail="facturé par Anthropic" testid="suivi-aide-cout" />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Puces testid="suivi-aide-vue" valeur={vue} onChange={setVue} options={[
              { valeur: "conversations", label: "Conversations", nb: lignes.length },
              { valeur: "sans-reponse", label: "Pas dans le guide", nb: questions.length },
            ]} />
            <Recherche valeur={cherche} onChange={setCherche} largeur={280}
              placeholder="Question, personne ou écran…" testid="suivi-aide-recherche" />
          </div>

          {vue === "conversations" ? (
            <Section titre="Conversations" aide="De la plus récente à la plus ancienne. Clique sur une ligne pour relire toute la conversation." testid="suivi-aide-liste">
              <Tableau
                testid="tableau-suivi-aide"
                entetes={["Quand", "Qui", "Écran", "Première question", { label: "Questions", droite: true }, "Pas dans le guide", { label: "Coût", droite: true }]}
                lignes={lignes}
                vide="Aucune conversation pour l'instant."
                onClic={ouvrir}
                rendu={(c) => (
                  <>
                    <Cellule muet><span className="whitespace-nowrap">{quand(c.maj)}</span></Cellule>
                    <Cellule fort>{c.par || "—"}</Cellule>
                    <Cellule muet>{(c.ecrans || []).join(", ") || "—"}</Cellule>
                    <Cellule>{c.premiere || "—"}</Cellule>
                    <Cellule droite>{c.nb_questions}</Cellule>
                    <Cellule>{c.sans_reponse ? <Badge ton="ambre">{c.sans_reponse}</Badge> : <span className="text-[var(--muted)]">—</span>}</Cellule>
                    <Cellule droite muet>{dollars(c.cout)}</Cellule>
                  </>
                )}
              />
            </Section>
          ) : (
            <Section titre="Pas dans le guide" aide="Les questions auxquelles l'Aide n'a pas trouvé de réponse dans le guide : à expliquer dans le guide de la console." testid="suivi-aide-questions-liste">
              <Tableau
                testid="tableau-suivi-aide-sans-reponse"
                entetes={["Quand", "Qui", "Question"]}
                lignes={questions}
                vide="Rien de manquant : l'Aide a trouvé toutes ses réponses."
                rendu={(q) => (
                  <>
                    <Cellule muet><span className="whitespace-nowrap">{quand(q.le)}</span></Cellule>
                    <Cellule fort>{q.par || "—"}</Cellule>
                    <Cellule>{q.question}</Cellule>
                  </>
                )}
              />
            </Section>
          )}
        </>
      )}

      {ouverte && (
        <Modale large titre="Conversation avec l'Aide" testid="suivi-aide-detail" onFermer={() => setOuverte(null)}
          sous_titre={[ouverte.par, quand(ouverte.debut), dollars(ouverte.cout)].filter(Boolean).join(" · ")}>
          <div className="space-y-4">
            {(ouverte.sans_reponse || []).length > 0 && (
              <div className="rounded-xl px-4 py-3 text-sm" style={{ background: "#FFFBEB", color: "#92400E" }}>
                <strong>Pas dans le guide :</strong> {ouverte.sans_reponse.join(" · ")}
              </div>
            )}
            {(ouverte.messages || []).map((m, i) => (
              <div key={i} className="rounded-xl border px-4 py-3"
                style={m.role === "user"
                  ? { borderColor: "#BFDBFE", background: "#EFF6FF" }
                  : { borderColor: "var(--border)", background: "#FAFCFD" }}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-1">
                  {m.role === "user" ? `Question${m.ecran ? ` · ${m.ecran}` : ""}` : "Réponse de l'Aide"} · {quand(m.le)}
                </div>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.texte}</div>
              </div>
            ))}
            <div className="flex justify-end">
              <button className="btn-ghost" onClick={() => setOuverte(null)}>Fermer</button>
            </div>
          </div>
        </Modale>
      )}
    </div>
  );
}
